import {
  DataTableColumn,
  SortDirection,
  ASC_SORT_DIRECTION,
  DESC_SORT_DIRECTION,
  NO_SORT_DIRECTION,
  getCellLeftPosition,
} from '@shared/ui/data-table';
import { UnstyledButton } from '@shared/ui/unstyled-button';
import { FilterLinesIcon, SortTriangleIcon } from '@shared/assets/icons';
import { colors } from '@shared/tokens/colors.stylex';
import * as stylex from '@stylexjs/stylex';
import { styles } from './styles';
import { useState } from 'react';

interface ColumnsProps<T extends object> {
  columns: DataTableColumn<T>[];
  sortClickHandler: (
    column: DataTableColumn<T>,
    sortMode: SortDirection,
    activeSorts?: { column: Extract<keyof T, string>; direction: Exclude<SortDirection, null> }[]
  ) => void;
  gridTemplateColumns: string;
  multiSort?: boolean;
}

export const Columns = <T extends object>({
  columns,
  sortClickHandler,
  gridTemplateColumns,
  multiSort = false,
}: ColumnsProps<T>) => {
  const [activeSorts, setActiveSorts] = useState<
    { column: Extract<keyof T, string>; direction: Exclude<SortDirection, null> }[]
  >([]);

  const getNextSortDirection = (currentDirection: SortDirection): SortDirection => {
    if (currentDirection === NO_SORT_DIRECTION) {
      return ASC_SORT_DIRECTION;
    }

    if (currentDirection === ASC_SORT_DIRECTION) {
      return DESC_SORT_DIRECTION;
    }

    return NO_SORT_DIRECTION;
  };

  const handleSortClick = (column: DataTableColumn<T>) => {
    const currentSort = activeSorts.find((item) => item.column === column.field);
    const newSortDirection = getNextSortDirection(currentSort?.direction ?? NO_SORT_DIRECTION);

    if (newSortDirection === NO_SORT_DIRECTION) {
      const nextActiveSorts = activeSorts.filter((item) => item.column !== column.field);

      setActiveSorts(nextActiveSorts);
      sortClickHandler(column, newSortDirection, nextActiveSorts);
      return;
    }

    if (!multiSort) {
      const nextActiveSorts = [{ column: column.field, direction: newSortDirection }];

      setActiveSorts(nextActiveSorts);
      sortClickHandler(column, newSortDirection, nextActiveSorts);
      return;
    }

    if (!currentSort) {
      const nextActiveSorts = [...activeSorts, { column: column.field, direction: newSortDirection }];

      setActiveSorts(nextActiveSorts);
      sortClickHandler(column, newSortDirection, nextActiveSorts);
      return;
    }

    const nextActiveSorts = activeSorts.map((item) =>
      item.column === column.field ? { column: column.field, direction: newSortDirection } : item
    );

    setActiveSorts(nextActiveSorts);
    sortClickHandler(column, newSortDirection, nextActiveSorts);
  };

  const getClassNames = (className: string) => [className, stylex.props(styles.columnHeader).className].join(' ');
  const isSortActive = (field: Extract<keyof T, string>, direction: Exclude<SortDirection, null>) =>
    activeSorts.some((item) => item.column === field && item.direction === direction);

  return (
    <div {...stylex.props(styles.columnsContainer)} style={{ gridTemplateColumns }}>
      {columns.map((column, index) => (
        <div
          key={index}
          {...stylex.props(styles.column, column.fixed && styles.columnFixed)}
          style={{ left: getCellLeftPosition(columns, column) }}
        >
          {column.renderHeader ? (
            <span className={getClassNames(column.headerClassName || '')}>{column.renderHeader()}</span>
          ) : (
            <span className={getClassNames(column.headerClassName || '')}>
              {column.headerName}

              <div {...stylex.props(styles.actions)}>
                {column.sortable && (
                  <>
                    <UnstyledButton {...stylex.props(styles.sortButton)} onClick={() => handleSortClick(column)}>
                      <SortTriangleIcon
                        {...stylex.props(isSortActive(column.field, ASC_SORT_DIRECTION) && styles.sortIconActive)}
                      />

                      <SortTriangleIcon
                        reverse
                        {...stylex.props(isSortActive(column.field, DESC_SORT_DIRECTION) && styles.sortIconActive)}
                      />
                    </UnstyledButton>
                    {column.showFilterIcon && (
                      <span {...stylex.props(styles.filterDropdownWrapper)}>
                        <UnstyledButton
                          {...stylex.props(styles.filterButton, column.isFilterActive && styles.filterButtonActive)}
                          onClick={column.onFilterClick}
                        >
                          <FilterLinesIcon color={column.isFilterActive ? colors.accentIcon : undefined} />
                          {column.isFilterActive && <span {...stylex.props(styles.filterActiveDot)} />}
                        </UnstyledButton>
                        {column.isFilterOpen && column.filterDropdown}
                      </span>
                    )}
                  </>
                )}
              </div>
            </span>
          )}
        </div>
      ))}
    </div>
  );
};
